import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@timely/ui';
import { supabase } from '../lib/supabaseClient';
import { publicConfig } from '../config/env';
import { callbackUrl } from '../auth/session';
import { useSession } from '../auth/SessionProvider';
import {
    CHECK_YOUR_EMAIL,
    PASSWORD_RULES,
    isEmailShaped,
    newPasswordError,
    normaliseEmail,
    passwordProblems,
    resetRequestError,
} from '../auth/authErrors';
import { AuthLayout, AuthLink, Field, FormNotice } from './AuthLayout';

/**
 * Asking for a reset link.
 *
 * Answers identically whether or not the address is registered, including when
 * the address is obviously malformed — anything else turns this form into a way
 * of testing which addresses exist. Only a rate limit or an unreachable server
 * is reported honestly, because neither says anything about the account.
 */
export function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [sent, setSent] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (busy) return;
        setError(null);

        if (!isEmailShaped(email)) {
            setSent(true);
            return;
        }

        setBusy(true);
        const { error: authError } = await supabase().auth.resetPasswordForEmail(
            normaliseEmail(email),
            { redirectTo: callbackUrl(publicConfig().appUrl, '/reset-password') },
        );
        setBusy(false);

        const message = resetRequestError(authError);
        if (message) {
            setError(message);
            return;
        }
        setSent(true);
    };

    if (sent) {
        return (
            <AuthLayout title="Check your email" lede={CHECK_YOUR_EMAIL}>
                <p className="text-[13.5px] text-label-2 leading-relaxed">
                    The link signs you in just long enough to choose a new password.
                </p>
                <div className="mt-6"><AuthLink to="/login">Back to sign in</AuthLink></div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout
            title="Reset your password"
            lede="Tell us the address you sign in with and we will send a link."
            footer={<AuthLink to="/login">Back to sign in</AuthLink>}
        >
            <form onSubmit={submit} noValidate>
                {error && <FormNotice tone="error">{error}</FormNotice>}
                <Field
                    label="Email"
                    name="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                />
                <Button type="submit" variant="primary" className="w-full mt-2" disabled={busy}>
                    {busy ? 'Sending…' : 'Send the link'}
                </Button>
            </form>
        </AuthLayout>
    );
}

/**
 * Choosing a new password, having arrived from the emailed link.
 *
 * The link puts a recovery session in place, so this screen is only usable by
 * somebody holding it. If there is no session the link was stale or already
 * used, and saying so plainly is better than a form that fails on submit.
 */
export function ResetPassword() {
    const navigate = useNavigate();
    const { state, loading, refresh } = useSession();
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => { void refresh(); }, [refresh]);

    const problems = passwordProblems(password);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (busy) return;
        setError(null);
        if (problems.length > 0) {
            setError('That password does not meet the rules below.');
            return;
        }
        setBusy(true);
        const { error: authError } = await supabase().auth.updateUser({ password });
        setBusy(false);
        if (authError) {
            setError(newPasswordError(authError));
            return;
        }
        setDone(true);
    };

    if (done) {
        return (
            <AuthLayout title="Password changed" lede="You are signed in with it now.">
                <Button variant="primary" className="w-full" onClick={() => navigate('/', { replace: true })}>
                    Continue
                </Button>
            </AuthLayout>
        );
    }

    if (!loading && state !== 'active') {
        return (
            <AuthLayout
                title="That link is no longer valid"
                lede="Reset links are good for one hour and can be used once."
                footer={<AuthLink to="/login">Back to sign in</AuthLink>}
            >
                <Button variant="primary" className="w-full" onClick={() => navigate('/forgot-password')}>
                    Send a new link
                </Button>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout title="Choose a new password">
            <form onSubmit={submit} noValidate>
                {error && <FormNotice tone="error">{error}</FormNotice>}
                <Field
                    label="New password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    hint={
                        <ul className="space-y-0.5" aria-label="Password rules">
                            {PASSWORD_RULES.map(rule => {
                                const failing = problems.includes(rule);
                                return (
                                    <li key={rule} className={failing ? 'text-label-3' : 'text-pos'}>
                                        {failing ? '○' : '✓'} {rule}
                                    </li>
                                );
                            })}
                        </ul>
                    }
                />
                <Button type="submit" variant="primary" className="w-full mt-2" disabled={busy}>
                    {busy ? 'Saving…' : 'Save the new password'}
                </Button>
            </form>
        </AuthLayout>
    );
}
